/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import { Fragment, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import H4 from 'UIComponents/elements/headings/H4';
import UICardSection from 'UIComponents/card/UICardSection';
import UIFlex from 'UIComponents/layout/UIFlex';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import Rating from 'marketplace-ui-reviews/components/Rating';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import { ProfileReviewWizard } from './ProfileReviewWizard';
import ProfileCardWrapper from './ProfileCardWrapper';
import WriteReviewButton from './WriteReviewButton';
import ReviewsCardBody from './ReviewsCardBody';
import { getIsPublicApp } from '../../selectors/app';
import { replace } from '../../utils/history';
import { getReviewFilters } from '../../selectors/reviews';
import { getAppContextCoreType } from '../../selectors/app';
import { getIsUngatedForReviewEditing } from '../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_BASE = 'ecosystem-marketplace-solutions-ui.profile.reviews';
const ReviewsCard = ({
  isPreview,
  onTrackInteraction,
  profileListing,
  reviewButtonRef,
  scrollToTabSection
}) => {
  const {
    pathname
  } = useLocation();
  const {
    name,
    reviewSummary,
    slug
  } = profileListing;
  const isReviewLink = pathname.includes('/write-review');
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [reviewPending, setReviewPending] = useState(false);
  const isPublicApp = useSelector(getIsPublicApp);
  const appContext = useSelector(getAppContextCoreType);
  const reviewFilters = useSelector(getReviewFilters);
  const isUngatedForReviewEditing = useSelector(getIsUngatedForReviewEditing);
  const {
    reviews: userReviews
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      entityId: profileListing.id,
      reviewerPortalId: PortalIdParser.get()
    }),
    offeringId: profileListing.id,
    reviewType: 'PROFILE'
  });
  const userReview = userReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!userReview && reviewPending,
    entityId: profileListing.id,
    entityPortalId: profileListing.portalId,
    isPublicApp,
    reviewPending: !userReview && reviewPending,
    reviewType: 'PROFILE'
  });
  const reviewabilityStatus = isPublicApp || isPreview ? RequestState.SUCCEEDED : reviewabilityRequestStatus;
  const {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId: profileListing.id,
    reviewType: 'PROFILE'
  });
  const renderedReviewButton = /*#__PURE__*/_jsx(WriteReviewButton, {
    hasEditableReview: isUngatedForReviewEditing && !!userReview,
    isPreview: isPreview,
    isPublicApp: isPublicApp,
    profileListing: profileListing,
    reviewButtonRef: reviewButtonRef,
    setWizardOpen: setWizardOpen,
    size: "md",
    unreviewableReason: unreviewableReason
  });
  const renderedBody = useMemo(() => reviewSummary.get('reviewCount') ? /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewStatsFetchStatus,
    children: /*#__PURE__*/_jsx(ReviewsCardBody, {
      reviews: reviews,
      reviewStats: reviewStats,
      reviewsFetchStatus: reviewsFetchStatus,
      reviewsTotal: reviewsTotal,
      reviewsCount: reviewsCount,
      onTrackInteraction: onTrackInteraction,
      scrollToTabSection: scrollToTabSection
    })
  }) : /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(H4, {
      className: "m-top-8",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.emptyState`
      })
    }), /*#__PURE__*/_jsx(Rating, {
      rating: 0,
      labelSize: "large",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.reviewCount`,
        options: {
          count: 0
        }
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "m-top-8",
      children: renderedReviewButton
    })]
  }), [onTrackInteraction, profileListing, renderedReviewButton, reviewSummary, reviewStatsFetchStatus, scrollToTabSection]);
  const {
    headingWrapperProps
  } = useResponsiveProps({
    headingWrapperProps: {
      xs: {
        justify: 'start',
        direction: 'column'
      },
      md: {
        justify: 'between',
        direction: 'row'
      }
    }
  });
  const renderedHeading = useMemo(() => /*#__PURE__*/_jsxs(UIFlex, Object.assign({}, headingWrapperProps, {
    children: [/*#__PURE__*/_jsx(H2, {
      className: "is--heading-3",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.title`,
        options: {
          name
        }
      })
    }), reviewSummary.get('reviewCount') ? renderedReviewButton : null]
  })), [headingWrapperProps, name, reviewSummary, renderedReviewButton]);
  return /*#__PURE__*/_jsxs(LoadingWrapper, {
    requestStatus: reviewabilityStatus,
    children: [/*#__PURE__*/_jsx(ProfileCardWrapper, {
      id: "reviews",
      children: /*#__PURE__*/_jsxs(UICardSection, {
        children: [renderedHeading, renderedBody]
      })
    }), wizardOpen && /*#__PURE__*/_jsx(ProfileReviewWizard, {
      editedReview: userReview,
      onConfirm: () => {
        setReviewPending(true);
        setWizardOpen(false);
      },
      onClose: () => {
        replace(`/${slug}`);
        setWizardOpen(false);
      },
      onTrackInteraction: onTrackInteraction,
      profileListing: profileListing,
      unreviewableReason: unreviewableReason
    })]
  });
};
ReviewsCard.propTypes = {
  isPreview: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  reviewButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),
  scrollToTabSection: PropTypes.func.isRequired
};
export default ReviewsCard;